import { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { resetWindowScrollTop, scrollTourTargetToPageTop } from '../utils/productTourPlacement';

export const TOUR_TARGET_FOCUS_EVENT = 'app:tour-target-focus';
export const TOUR_TARGET_BLUR_EVENT = 'app:tour-target-blur';
/** Fired when a page tour finishes or is skipped. */
export const TOUR_PAGE_END_EVENT = 'app:tour-page-end';

export interface TourTargetEventDetail {
  pageKey: string;
  targetKey: string;
  /** Next step target when blur is part of an in-tour step change. */
  nextTargetKey?: string;
}

export interface TourPageEndEventDetail {
  pageKey: string;
}

export function dispatchTourPageEnd(pageKey: string): void {
  window.dispatchEvent(
    new CustomEvent<TourPageEndEventDetail>(TOUR_PAGE_END_EVENT, {
      detail: { pageKey },
    })
  );
}

export function dispatchTourTargetFocus(pageKey: string, targetKey: string): void {
  window.dispatchEvent(
    new CustomEvent<TourTargetEventDetail>(TOUR_TARGET_FOCUS_EVENT, {
      detail: { pageKey, targetKey },
    })
  );
}

export function dispatchTourTargetBlur(
  pageKey: string,
  targetKey: string,
  nextTargetKey?: string
): void {
  window.dispatchEvent(
    new CustomEvent<TourTargetEventDetail>(TOUR_TARGET_BLUR_EVENT, {
      detail: { pageKey, targetKey, nextTargetKey },
    })
  );
}

export const SIDE_NAV_DRAWER_TARGET_KEYS = new Set([
  'side-nav-panel',
  'side-nav-public-pages',
  'side-nav-leitner',
  'side-nav-telegram',
  'side-nav-chat',
]);

/** Keep the side drawer open when the tour moves between steps inside it. */
export function shouldKeepTourTargetOpenAcrossStep(
  leavingTargetKey: string,
  nextTargetKey?: string
): boolean {
  if (!nextTargetKey) return false;
  return (
    SIDE_NAV_DRAWER_TARGET_KEYS.has(leavingTargetKey) &&
    SIDE_NAV_DRAWER_TARGET_KEYS.has(nextTargetKey)
  );
}

/**
 * Expand a collapsible UI region while a tour step focuses it; restore prior state on blur.
 */
export function useTourCollapsibleTarget(
  targetKey: string,
  isOpen: boolean,
  setOpen: (open: boolean) => void,
  options?: { restoreOnBlur?: boolean; restoreOnPageEnd?: boolean }
): void {
  const restoreOnBlur = options?.restoreOnBlur !== false;
  const restoreOnPageEnd = options?.restoreOnPageEnd === true;
  const wasOpenRef = useRef<boolean | null>(null);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  useEffect(() => {
    const onFocus = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (detail?.targetKey !== targetKey) return;
      if (wasOpenRef.current === null) {
        wasOpenRef.current = isOpenRef.current;
      }
      if (!isOpenRef.current) {
        flushSync(() => setOpen(true));
      }
    };

    const onBlur = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (detail?.targetKey !== targetKey) return;
      const keepOpenAcross = shouldKeepTourTargetOpenAcrossStep(
        targetKey,
        detail.nextTargetKey
      );
      if (restoreOnBlur && wasOpenRef.current === false && !keepOpenAcross) {
        setOpen(false);
      }
      if (!restoreOnPageEnd && !keepOpenAcross) {
        wasOpenRef.current = null;
      }
    };

    const onTourEnd = () => {
      if (!restoreOnPageEnd) return;
      const wasOpen = wasOpenRef.current;
      if (targetKey === 'library-filters') {
        scrollTourTargetToPageTop('library-header', 'auto');
        resetWindowScrollTop('auto');
      }
      window.setTimeout(() => {
        if (wasOpen === false) setOpen(false);
        wasOpenRef.current = null;
        if (targetKey === 'library-filters') {
          window.setTimeout(() => {
            scrollTourTargetToPageTop('library-header', 'auto');
            resetWindowScrollTop('auto');
          }, 360);
        }
      }, 40);
    };

    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    window.addEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
    window.addEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    return () => {
      window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
      window.removeEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
      window.removeEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    };
  }, [targetKey, restoreOnBlur, restoreOnPageEnd, setOpen]);
}

/**
 * Open one of several tour targets, and close it when the tour leaves the group.
 * One shared "was open" memory so a later step does not think the user opened it.
 */
export function useTourCollapsibleTargetGroup(
  targetKeys: ReadonlySet<string>,
  isOpen: boolean,
  setOpen: (open: boolean) => void
): void {
  const wasOpenRef = useRef<boolean | null>(null);
  const isOpenRef = useRef(isOpen);
  const keysRef = useRef(targetKeys);
  isOpenRef.current = isOpen;
  keysRef.current = targetKeys;

  useEffect(() => {
    const matches = (key: string) => keysRef.current.has(key);

    const onFocus = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (!detail?.targetKey || !matches(detail.targetKey)) return;
      if (wasOpenRef.current === null) {
        wasOpenRef.current = isOpenRef.current;
      }
      if (!isOpenRef.current) {
        flushSync(() => setOpen(true));
      }
    };

    const onBlur = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (!detail?.targetKey || !matches(detail.targetKey)) return;
      const keepOpenAcross = Boolean(detail.nextTargetKey && matches(detail.nextTargetKey));
      if (wasOpenRef.current === false && !keepOpenAcross) {
        setOpen(false);
      }
      if (!keepOpenAcross) {
        wasOpenRef.current = null;
      }
    };

    const onTourEnd = () => {
      if (wasOpenRef.current === false) setOpen(false);
      wasOpenRef.current = null;
    };

    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    window.addEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
    window.addEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    return () => {
      window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
      window.removeEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
      window.removeEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    };
  }, [setOpen]);
}

/**
 * Keep a target closed during its tour step (e.g. side menu button showing |||).
 * Restores prior open state on blur unless the tour ended for this page.
 */
export function useTourEnsureClosedTarget(
  targetKey: string,
  isOpen: boolean,
  setOpen: (open: boolean) => void
): void {
  const wasOpenRef = useRef<boolean | null>(null);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  useEffect(() => {
    const onFocus = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (detail?.targetKey !== targetKey) return;
      if (wasOpenRef.current === null) {
        wasOpenRef.current = isOpenRef.current;
      }
      if (isOpenRef.current) {
        flushSync(() => setOpen(false));
      }
    };

    const onBlur = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (detail?.targetKey !== targetKey) return;
      if (wasOpenRef.current === true) setOpen(true);
      wasOpenRef.current = null;
    };

    const onTourEnd = (event: Event) => {
      wasOpenRef.current = null;
    };

    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    window.addEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
    window.addEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    return () => {
      window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
      window.removeEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
      window.removeEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    };
  }, [targetKey, setOpen]);
}

/**
 * Same as useTourCollapsibleTarget but matches target keys by prefix (e.g. bottom-banner-*).
 */
export function useTourCollapsibleTargetPrefix(
  prefix: string,
  isOpen: boolean,
  setOpen: (open: boolean) => void
): void {
  const wasOpenRef = useRef<boolean | null>(null);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  useEffect(() => {
    const matches = (key: string) => key.startsWith(prefix);

    const onFocus = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (!detail?.targetKey || !matches(detail.targetKey)) return;
      if (wasOpenRef.current === null) {
        wasOpenRef.current = isOpenRef.current;
      }
      if (!isOpenRef.current) {
        flushSync(() => setOpen(true));
      }
    };

    const onBlur = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (!detail?.targetKey || !matches(detail.targetKey)) return;
      const keepOpenAcross = shouldKeepTourTargetOpenAcrossStep(
        detail.targetKey,
        detail.nextTargetKey
      );
      if (wasOpenRef.current === false && !keepOpenAcross) {
        setOpen(false);
      }
      if (!keepOpenAcross) {
        wasOpenRef.current = null;
      }
    };

    const onTourEnd = () => {
      if (wasOpenRef.current === false) setOpen(false);
      wasOpenRef.current = null;
    };

    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    window.addEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
    window.addEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    return () => {
      window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
      window.removeEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
      window.removeEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    };
  }, [prefix, setOpen]);
}
